// src/pages/sow-draft/GenerationProgressBar.jsx
//
// Progress for a draft generation run.
//
// The previous version showed a percentage it invented -- 3% per poll,
// capped at 95 -- because the status endpoint returns only
// {draft_id, status, generation_error}. A number that looks measured but
// isn't is worse than no number: it implies a completion estimate nobody
// can make, and it always stalls at 95% right when the user starts
// wondering whether the run has hung.
//
// Per-section progress is not merely unexposed, it does not exist yet: the
// pipeline generates sections concurrently (SECTION_CONCURRENCY) and
// persists once at the end, so there is no partial state to report. Until
// the backend emits per-section completion, the honest display is the
// phase the run is in plus how long it has been going.
//
// `percent` stays in the props for the determinate phases the wizard does
// know (creating the draft, finalising); passing null selects the
// indeterminate bar.

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import "./generationProgress.css";

/** mm:ss since `startedAt`, ticking once a second. */
function useElapsed(startedAt) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  if (!startedAt) return null;
  const totalSeconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export default function GenerationProgressBar({ section, percent, startedAt, sectionCount }) {
  const elapsed = useElapsed(startedAt);
  const isIndeterminate = percent === null || percent === undefined;
  const pct = isIndeterminate ? null : Math.round(Math.min(100, Math.max(0, percent)));

  return (
    <div className="flex flex-col gap-1.5 mb-4" aria-live="polite">
      <div
        className="h-2 rounded-full bg-gray-100 overflow-hidden"
        role="progressbar"
        aria-valuenow={pct ?? undefined}
        aria-valuemin={pct === null ? undefined : 0}
        aria-valuemax={pct === null ? undefined : 100}
        aria-valuetext={pct === null ? `${section} — in progress` : undefined}
      >
        {isIndeterminate ? (
          // A travelling band: motion signals "still working" without
          // claiming a position. Respects prefers-reduced-motion in CSS.
          <div className="sow-progress-indeterminate h-full rounded-full bg-brand-600" />
        ) : (
          <div
            className="h-full rounded-full bg-brand-600 transition-all duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <div className="flex justify-between text-xs text-muted">
        <span>
          {section}
          {isIndeterminate && sectionCount ? ` · ${sectionCount} sections` : ""}
        </span>
        <span>{pct === null ? elapsed || "" : `${pct}%`}</span>
      </div>
    </div>
  );
}

GenerationProgressBar.propTypes = {
  section: PropTypes.string,
  /** null/undefined renders the indeterminate bar; a number renders a width. */
  percent: PropTypes.number,
  /** Date.now() when the run began, for the elapsed clock. */
  startedAt: PropTypes.number,
  sectionCount: PropTypes.number,
};
